import {
  addSafeTransferRecipientResponseSchema,
  addSafeTransferRecipientSchema,
  createSafeTransferResponseSchema,
  createSafeTransferSchema,
  deleteSafeTransferFileResponseSchema,
  getRecipientMeResponseSchema,
  getSafeTransferAuditResponseSchema,
  getSafeTransferResponseSchema,
  listSafeTransfersPaginatedResponseSchema,
  safeTransferClaimCodeResponseSchema,
  safeTransferIdAustriaNonceResponseSchema,
  safeTransferUnlockRequestSchema,
  safeTransferUnlockResponseSchema,
  safeTransferVerifyIdAustriaRequestSchema,
  safeTransferVerifyIdAustriaResponseSchema,
  safeTransferVerifyWebidStartRequestSchema,
  safeTransferVerifyWebidStartResponseSchema,
  notFoundResponseSchema,
  permissionDeniedResponseSchema,
  removeSafeTransferRecipientResponseSchema,
  revokeSafeTransferResponseSchema,
  safeTransferNotEditableResponseSchema,
  safeTransferPricingResponseSchema,
  safeTransferNotFoundResponseSchema,
  safeTransferRecipientDuplicatedResponseSchema,
  safeTransferRecipientNotFoundResponseSchema,
  sendSafeTransferResponseSchema,
  updateSafeTransferResponseSchema,
  updateSafeTransferSchema,
  badRequestResponseSchema,
} from 'xosign-types';
import { z } from 'zod';
import { API_URL_BASE } from '@/config';
import { type EndpointDefinition } from '@/types/endpointTypes';

// Recipient-side errors come from the token middleware as well as handlers,
// with varying shapes — validate loosely and branch on `code` at the call site.
const recipientErrorSchema = z.object({
  success: z.boolean(),
  code: z.string().optional(),
  message: z.string().optional(),
});
const unlockErrorSchema = recipientErrorSchema.extend({
  attemptsLeft: z.number().optional(),
});

const CRUD = {
  r: z.object({ transferId: z.string() }),
  u: updateSafeTransferSchema.extend({ transferId: z.string() }),
  addRecipient: addSafeTransferRecipientSchema.extend({
    transferId: z.string(),
  }),
  removeRecipient: z.object({
    transferId: z.string(),
    recipientId: z.string(),
  }),
  deleteFile: z.object({ transferId: z.string(), fileId: z.string() }),
};

const endpoints = {
  'safeTransfers.get.all': {
    method: 'GET',
    url: `${API_URL_BASE}safe-transfers`,
    requestSchema: z.undefined(),
    responseSuccessSchema: listSafeTransfersPaginatedResponseSchema,
    requiresToken: 'local',
    responseErrorSchemas: z.undefined(),
  } satisfies EndpointDefinition<
    z.ZodUndefined,
    typeof listSafeTransfersPaginatedResponseSchema,
    z.ZodUndefined
  >,
  'safeTransfers.pricing': {
    method: 'GET',
    url: `${API_URL_BASE}safe-transfers/pricing/:recipientCount`,
    requestSchema: z.object({ recipientCount: z.string() }),
    responseSuccessSchema: safeTransferPricingResponseSchema,
    requiresToken: 'local',
    responseErrorSchemas: z.undefined(),
  } satisfies EndpointDefinition<
    z.ZodObject<{ recipientCount: z.ZodString }>,
    typeof safeTransferPricingResponseSchema,
    z.ZodUndefined
  >,
  'safeTransfers.audit': {
    method: 'GET',
    url: `${API_URL_BASE}safe-transfers/:transferId/audit`,
    requestSchema: CRUD.r,
    responseSuccessSchema: getSafeTransferAuditResponseSchema,
    requiresToken: 'local',
    responseErrorSchemas: {
      404: safeTransferNotFoundResponseSchema,
    },
  } satisfies EndpointDefinition<
    typeof CRUD.r,
    typeof getSafeTransferAuditResponseSchema,
    {
      404: typeof safeTransferNotFoundResponseSchema;
    }
  >,
  'safeTransfers.recipient.me': {
    method: 'GET',
    url: `${API_URL_BASE}safe-transfers/recipient/me`,
    requestSchema: z.undefined(),
    responseSuccessSchema: getRecipientMeResponseSchema,
    requiresToken: 'session',
    responseErrorSchemas: {
      401: recipientErrorSchema,
      404: recipientErrorSchema,
      410: recipientErrorSchema,
    },
  } satisfies EndpointDefinition<
    z.ZodUndefined,
    typeof getRecipientMeResponseSchema,
    {
      401: typeof recipientErrorSchema;
      404: typeof recipientErrorSchema;
      410: typeof recipientErrorSchema;
    }
  >,
  'safeTransfers.recipient.idAustriaNonce': {
    method: 'POST',
    url: `${API_URL_BASE}safe-transfers/recipient/idaustria-nonce`,
    requestSchema: z.undefined(),
    responseSuccessSchema: safeTransferIdAustriaNonceResponseSchema,
    requiresToken: 'session',
    responseErrorSchemas: z.undefined(),
  } satisfies EndpointDefinition<
    z.ZodUndefined,
    typeof safeTransferIdAustriaNonceResponseSchema,
    z.ZodUndefined
  >,
  'safeTransfers.recipient.verifyIdAustria': {
    method: 'POST',
    url: `${API_URL_BASE}safe-transfers/recipient/verify-idaustria`,
    requestSchema: safeTransferVerifyIdAustriaRequestSchema,
    responseSuccessSchema: safeTransferVerifyIdAustriaResponseSchema,
    requiresToken: 'session',
    responseErrorSchemas: z.undefined(),
  } satisfies EndpointDefinition<
    typeof safeTransferVerifyIdAustriaRequestSchema,
    typeof safeTransferVerifyIdAustriaResponseSchema,
    z.ZodUndefined
  >,
  'safeTransfers.recipient.webidStart': {
    method: 'POST',
    url: `${API_URL_BASE}safe-transfers/recipient/verify-webid/start`,
    requestSchema: safeTransferVerifyWebidStartRequestSchema,
    responseSuccessSchema: safeTransferVerifyWebidStartResponseSchema,
    requiresToken: 'session',
    responseErrorSchemas: z.undefined(),
  } satisfies EndpointDefinition<
    typeof safeTransferVerifyWebidStartRequestSchema,
    typeof safeTransferVerifyWebidStartResponseSchema,
    z.ZodUndefined
  >,
  'safeTransfers.recipient.claimCode': {
    method: 'POST',
    url: `${API_URL_BASE}safe-transfers/recipient/claim-code`,
    requestSchema: z.undefined(),
    responseSuccessSchema: safeTransferClaimCodeResponseSchema,
    requiresToken: 'session',
    responseErrorSchemas: z.undefined(),
  } satisfies EndpointDefinition<
    z.ZodUndefined,
    typeof safeTransferClaimCodeResponseSchema,
    z.ZodUndefined
  >,
  'safeTransfers.recipient.unlock': {
    method: 'POST',
    url: `${API_URL_BASE}safe-transfers/recipient/unlock`,
    requestSchema: safeTransferUnlockRequestSchema,
    responseSuccessSchema: safeTransferUnlockResponseSchema,
    requiresToken: 'session',
    responseErrorSchemas: {
      400: unlockErrorSchema,
      409: recipientErrorSchema,
      423: recipientErrorSchema,
    },
  } satisfies EndpointDefinition<
    typeof safeTransferUnlockRequestSchema,
    typeof safeTransferUnlockResponseSchema,
    {
      400: typeof unlockErrorSchema;
      409: typeof recipientErrorSchema;
      423: typeof recipientErrorSchema;
    }
  >,
  'safeTransfers.create': {
    method: 'POST',
    url: `${API_URL_BASE}safe-transfers`,
    requestSchema: createSafeTransferSchema,
    responseSuccessSchema: createSafeTransferResponseSchema,
    requiresToken: 'local',
    responseErrorSchemas: {
      403: permissionDeniedResponseSchema,
    },
  } satisfies EndpointDefinition<
    typeof createSafeTransferSchema,
    typeof createSafeTransferResponseSchema,
    { 403: typeof permissionDeniedResponseSchema }
  >,
  'safeTransfers.get': {
    method: 'GET',
    url: `${API_URL_BASE}safe-transfers/:transferId`,
    requestSchema: CRUD.r,
    responseSuccessSchema: getSafeTransferResponseSchema,
    requiresToken: 'local',
    responseErrorSchemas: {
      403: permissionDeniedResponseSchema,
      404: safeTransferNotFoundResponseSchema,
    },
  } satisfies EndpointDefinition<
    typeof CRUD.r,
    typeof getSafeTransferResponseSchema,
    {
      403: typeof permissionDeniedResponseSchema;
      404: typeof safeTransferNotFoundResponseSchema;
    }
  >,
  'safeTransfers.update': {
    method: 'PATCH',
    url: `${API_URL_BASE}safe-transfers/:transferId`,
    requestSchema: CRUD.u,
    responseSuccessSchema: updateSafeTransferResponseSchema,
    requiresToken: 'local',
    responseErrorSchemas: {
      400: safeTransferNotEditableResponseSchema,
      404: safeTransferNotFoundResponseSchema,
    },
  } satisfies EndpointDefinition<
    typeof CRUD.u,
    typeof updateSafeTransferResponseSchema,
    {
      400: typeof safeTransferNotEditableResponseSchema;
      404: typeof safeTransferNotFoundResponseSchema;
    }
  >,
  'safeTransfers.send': {
    method: 'POST',
    url: `${API_URL_BASE}safe-transfers/:transferId/send`,
    requestSchema: CRUD.r,
    responseSuccessSchema: sendSafeTransferResponseSchema,
    requiresToken: 'local',
    responseErrorSchemas: {
      400: badRequestResponseSchema,
      404: safeTransferNotFoundResponseSchema,
    },
  } satisfies EndpointDefinition<
    typeof CRUD.r,
    typeof sendSafeTransferResponseSchema,
    {
      400: typeof badRequestResponseSchema;
      404: typeof safeTransferNotFoundResponseSchema;
    }
  >,
  'safeTransfers.revoke': {
    method: 'POST',
    url: `${API_URL_BASE}safe-transfers/:transferId/revoke`,
    requestSchema: CRUD.r,
    responseSuccessSchema: revokeSafeTransferResponseSchema,
    requiresToken: 'local',
    responseErrorSchemas: {
      400: badRequestResponseSchema,
      404: safeTransferNotFoundResponseSchema,
    },
  } satisfies EndpointDefinition<
    typeof CRUD.r,
    typeof revokeSafeTransferResponseSchema,
    {
      400: typeof badRequestResponseSchema;
      404: typeof safeTransferNotFoundResponseSchema;
    }
  >,
  'safeTransfers.recipients.add': {
    method: 'POST',
    url: `${API_URL_BASE}safe-transfers/:transferId/recipients`,
    requestSchema: CRUD.addRecipient,
    responseSuccessSchema: addSafeTransferRecipientResponseSchema,
    requiresToken: 'local',
    responseErrorSchemas: {
      400: safeTransferNotEditableResponseSchema,
      404: safeTransferNotFoundResponseSchema,
      409: safeTransferRecipientDuplicatedResponseSchema,
    },
  } satisfies EndpointDefinition<
    typeof CRUD.addRecipient,
    typeof addSafeTransferRecipientResponseSchema,
    {
      400: typeof safeTransferNotEditableResponseSchema;
      404: typeof safeTransferNotFoundResponseSchema;
      409: typeof safeTransferRecipientDuplicatedResponseSchema;
    }
  >,
  'safeTransfers.recipients.remove': {
    method: 'DELETE',
    url: `${API_URL_BASE}safe-transfers/:transferId/recipients/:recipientId`,
    requestSchema: CRUD.removeRecipient,
    responseSuccessSchema: removeSafeTransferRecipientResponseSchema,
    requiresToken: 'local',
    responseErrorSchemas: {
      400: safeTransferNotEditableResponseSchema,
      404: safeTransferRecipientNotFoundResponseSchema,
    },
  } satisfies EndpointDefinition<
    typeof CRUD.removeRecipient,
    typeof removeSafeTransferRecipientResponseSchema,
    {
      400: typeof safeTransferNotEditableResponseSchema;
      404: typeof safeTransferRecipientNotFoundResponseSchema;
    }
  >,
  'safeTransfers.files.delete': {
    method: 'DELETE',
    url: `${API_URL_BASE}safe-transfers/:transferId/files/:fileId`,
    requestSchema: CRUD.deleteFile,
    responseSuccessSchema: deleteSafeTransferFileResponseSchema,
    requiresToken: 'local',
    responseErrorSchemas: {
      400: safeTransferNotEditableResponseSchema,
      404: notFoundResponseSchema,
    },
  } satisfies EndpointDefinition<
    typeof CRUD.deleteFile,
    typeof deleteSafeTransferFileResponseSchema,
    {
      400: typeof safeTransferNotEditableResponseSchema;
      404: typeof notFoundResponseSchema;
    }
  >,
};

export default endpoints;
